import { closestEventHolder, dispatchCustomEvent } from './ui-helpers';
import type { EventHandler, MaybeHTMLElementOrParent } from './utils.type';
import { isNode } from './helpers';

interface EventHandlerOptions {
    name: string;
}

/**
 * @memberof SharedComponents
 * @classdesc Class for handling declarative events
 * @alias EventObserver
 * @example
 * const clickObserver = new EventObserver({name: 'click'});
 * clickObserver.observe(this);
 * this.addEventListener('open-record', (e) => console.log('Open record triggered!', e));
 * clickObserver.disconnect();
 */
export class EventObserver {
    _config: EventHandlerOptions;
    _target: EventTarget | null = null;

    private readonly internalEventHandler!: EventHandler;

    /**
     * @param {EventHandlerOptions} config - Configuration object
     */
    constructor(config: EventHandlerOptions) {
        this._config = config || { name: 'click' };
        this.internalEventHandler = this.handleInternalEvent.bind(this);
    }

    /**
     * This method filters all events out what are not related to declarative event.
     * What does not contain data-event attribute.
     * @param {Event} event
     * @private
     */
    private handleInternalEvent(event: Event): void {
        const target = event.target;
        if (!(target instanceof Element) || !isNode(this._target)) {
            return;
        }
        const relatedTarget = closestEventHolder(target, this._target);
        if (!relatedTarget) {
            return;
        }
        this.handleDeclarativeEvent(event, relatedTarget);
    }

    /**
     * This method transforms the declarative event defined in the attribute data-event="name"
     * and dispatches the custom event with the same name
     * @private
     * @param {Event} originalEvent
     * @param {Element} relatedTarget
     */
    private handleDeclarativeEvent(
        originalEvent: Event,
        relatedTarget: MaybeHTMLElementOrParent
    ) {
        // @ts-expect-error - fix nonexistent property error later
        if (originalEvent.processedAsDeclarative) {
            return;
        }
        // @ts-expect-error - fix nonexistent property error later
        originalEvent.processedAsDeclarative = true;
        const relatedTargetName = (relatedTarget as HTMLElement)?.dataset
            ?.event;
        if (!this._target || !relatedTargetName) {
            return;
        }
        dispatchCustomEvent(this._target, relatedTargetName, {
            relatedTarget: relatedTarget,
            originalEvent: originalEvent,
        });
    }

    /**
     * Start observing
     * @param {Element} target
     */
    observe(target: Element) {
        this._target = target;
        this._target.addEventListener(
            this._config.name,
            this.internalEventHandler
        );
    }

    /**
     * Destroy listener
     */
    disconnect() {
        this._target?.removeEventListener(
            this._config.name,
            this.internalEventHandler
        );
        this._target = null;
    }
}
